import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronLeft, Calendar, ChevronRight, ChevronDown,
  Target, Megaphone, Monitor, GraduationCap, Stethoscope, Briefcase,
  BarChart3, Users, DollarSign, TrendingUp, Image, Zap,
  Eye, PiggyBank, Award, HelpCircle, LineChart, CalendarCheck
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const WHATSAPP_URL = 'https://wa.me/5588996527079';

const audiences = [
  { icon: Megaphone, label: 'Agências de Marketing', desc: 'Que vendem serviços via reuniões e precisam provar ROI ao cliente.' },
  { icon: Monitor, label: 'SaaS / Softwares', desc: 'Com vendas consultivas e ciclo de fechamento por demonstração.' },
  { icon: GraduationCap, label: 'Escolas e Cursos', desc: 'De ticket alto que dependem de agendamentos para converter.' },
  { icon: Stethoscope, label: 'Clinicas e Consultórios', desc: 'Que investem em tráfego pago para captação de pacientes.' },
  { icon: Briefcase, label: 'Consultorias B2B', desc: 'Onde cada lead qualificado pode valer milhares em contrato.' }
];

const ecosystemItems = [
  {
    icon: Users,
    title: 'Planilha de Leads e Reuniões',
    description: 'Controle total de leads, reuniões agendadas vs. realizadas, fechamentos e metas. Simples de qualquer um preencher, poderosa nos insights.'
  },
  {
    icon: BarChart3,
    title: 'Foco no Funil de Conversão',
    description: 'Visão geral do funil: do clique ao fechamento. Veja taxas de conversão, gargalos e oportunidades em cada etapa e origem.'
  },
  {
    icon: DollarSign,
    title: 'Análise de Custos e Metas',
    description: 'Custo por Aquisição e outros (por lead, por reuniões) detalhados por origem. Acompanhe metas de vendas em tempo real.'
  }
];

const differentials = [
  {
    icon: Zap,
    title: 'Dados Gratuitos e Automáticos',
    description: 'Os dados de tráfego são obtidos de forma gratuita e automática via conectores direto dos gerenciadores.'
  },
   {
    icon: LineChart,
    title: 'Análise Profunda de Tráfego',
    description: 'Mergulhe nos dados de Meta Ads e Google Ads com visões segmentadas por campanha, conjunto e anúncio.'
  },
  {
    icon: Image,
    title: 'Análise dos Criativos',
    description: 'Página dedicada com thumbnails dos seus anúncios e métricas lado a lado. Descubra quais criativos geram mais retorno.'
  },
  
];

const benefits = [
  {
    icon: DollarSign,
    title: 'Onde estão os maiores custos',
    description: 'Identifique os gargalos e que etapa está sendo mais cara que o normal em seu funil de conversão.'
  },
  {
    icon: TrendingUp,
    title: 'Conversões Otimizadas',
    description: 'Veja quais etapas do funil e origens de leads convertem mais e replique o que funciona em escala.'
  },
  {
    icon: Award,
    title: 'Criativos com Mais Retorno',
    description: 'Saiba exatamente quais anúncios geram mais oportunidades de negócios e duplique os vencedores!'
  }
];

const faqs = [
  {
    question: 'De onde vem os dados de Leads, Reuniões e Vendas? Posso integrar com meu CRM?',
    answer: 'Como há diversos sistemas de CRMs diferentes, criei um modelo de planilha para preenchimento manual dos dados de forma fácil, mas se seu CRM tiver API ou Webhook, dá para integrar sim.'
  },
  {
    question: 'Tudo pode ser personalizado com as cores da minha agência?',
    answer: 'Com certeza! Não apenas as cores como também os gráficos, métricas, e tudo mais que você desejar; só ir sugerindo durante a implementação.'
  },
  {
    question: 'Quanto tempo leva para ter tudo funcionando?',
    answer: 'Dependendo do que desejar, entre alguns dias e uma semana. A configuração dos conectores de tráfego é feita junto com você ao fecharmos o negócio, nem preciso de acesso direto as contas.'
  },
  {
    question: 'Os dados de tráfego pago realmente são gratuitos e automáticos?',
    answer: 'Sim. Os conectores utilizados para Meta Ads e Google Ads são gratuitos e puxam os dados automaticamente direto para o Looker Studio, sem nem mesmo precisar de planilhas.'
  },
  {
    question: 'O que acontece após a entrega?',
    answer: 'Você contará com meu suporte contínuo para dúvidas, ajustes e melhorias pontuais, mesmo após a conclusão. Inclusive, pode pagar só após estar tudo 100% também!'
  }
];

const clientLogos = [
  'Okbox', 'Agência Meio Digital', 'Gooxie Group', 'Agência Turbo 7',
  'Agência BUS', 'Agência Acelera+', 'Agência Backstage', 'Audens Group', 'Agência Take One'
];

const showcaseImages = [
  '/assets/dashComercial.png',
  '/assets/dashMetaAds.png',
  '/assets/dashCriativos.png',
  '/assets/planilhaComercial.png'
];

const BiVendasPerformanceLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const nextImage = () => setCurrentImage((p) => (p + 1) % showcaseImages.length);
  const prevImage = () => setCurrentImage((p) => (p - 1 + showcaseImages.length) % showcaseImages.length);

  return (
    <>
      <Helmet>
        <title>BI de Vendas e Performance Comercial | Dashboards de Vendas</title>
        <meta name="description" content="Acompanhe seu funil de vendas, taxas de conversão e metas comerciais com dashboards automatizados e precisos." />
      </Helmet>
    <div className="page-bv-landing">

      {/* ===== HERO COMERCIAL OTIMIZADO ===== */}
<section className="bv-hero" data-testid="bv-hero" style={{ overflow: 'hidden', padding: '40px 0' }}>
  <div className="rl-container">
    <Link to="/services" className="back-link bv-back-link" style={{ marginBottom: '20px' }} data-testid="bv-back-link">
      <ArrowLeft size={18} />
      Voltar aos Serviços
    </Link>

    {/* GRID PAI: Usando a proporção que você gostou (1.4fr 0.6fr) */}
    <div className="bv-hero-grid" style={{ 
      display: 'grid', 
      gridTemplateColumns: '1.4fr 0.6fr', 
      gap: '50px', 
      alignItems: 'center',
      marginTop: '20px'
    }}>
      
      {/* Coluna da Esquerda: Texto Largo */}
      <div className="bv-hero-content">
        <div className="bv-hero-badge">Do Clique ao Fechamento</div>
        <h1 className="bv-hero-title" style={{ marginTop: '15px', fontSize: '2.4rem', lineHeight: '1.2' }}>
          <span className="bv-accent">Tenha clareza total</span> do seu setor comercial.
          Pare de adivinhar seus <span className="bv-accent">custos, </span>
          <span className="bv-accent">conversões e retornos</span>
        </h1>
        <p className="bv-hero-subtitle" style={{ marginTop: '20px', marginBottom: '30px', fontSize: '1.1rem', opacity: '0.9' }}>
          Uma solução de BI completa no Looker Studio para empresas que dependem de agendamentos para vender.
          Dashboards integrados com Ads, do anúncio à reunião fechada.
        </p>
        <div className="bv-hero-cta-group">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="bv-btn-primary btn-large" data-testid="bv-hero-cta">
            Quero Clareza nas Minhas Vendas
            <ArrowRight size={20} />
          </a>
        </div>
      </div>

      {/* Coluna da Direita: Funil Centralizado na sua área */}
      <div className="bv-hero-visual hide-on-mobile" aria-hidden="true" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        paddingRight: '20px',
        width: '100%' 
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '350px', alignItems: 'center' }}>
          
          <div style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '120%', border: '1px solid rgba(255,255,255,0.05)' }}>
            <Eye size={18} color="#94a3b8" /> <span style={{fontSize: '12px', fontWeight: '600' }}>Cliques no Link</span>
          </div>

          <div style={{ backgroundColor: '#1e1b4b', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '110%' }}>
            <Users size={18} color="#818cf8" /> <span style={{fontSize: '12px', fontWeight: '600' }}>Leads Qualificados</span>
          </div>

          <div style={{ backgroundColor: '#064e3b', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%' }}>
            <Calendar size={18} color="#34d399" /> <span style={{fontSize: '12px', fontWeight: '600' }}>Agendamentos</span>
          </div>

          <div style={{ backgroundColor: '#064e3b', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '90%' }}>
            <CalendarCheck size={18} color="#34d399" /> <span style={{fontSize: '12px', fontWeight: '600' }}>Reuniões</span>
          </div>

          <div style={{ backgroundColor: '#fbbf24', padding: '15px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '80%' }}>
            <DollarSign size={18} color="#000" /> <span style={{fontSize: '12px', fontWeight: '600', color: '#000' }}>Vendas</span>
          </div>

        </div>
      </div>

    </div>
  </div>
</section>

      {/* ===== PARA QUEM É ===== */}
      <section className="bv-audience" data-testid="bv-audience">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">Para Quem É Essa Solução?</h2>
            <p className="bv-section-subtitle">
              Negócios que dependem de reuniões e agendamentos para converter vendas
            </p>
          </div>
          <div className="bv-audience-grid">
            {audiences.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bv-audience-card" data-testid={`bv-audience-${i}`}>
                  <div className="bv-audience-icon">
                    <Icon size={24} />
                  </div>
                  <h3 className="bv-audience-label">{item.label}</h3>
                  <p className="bv-audience-desc">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== ECOSSISTEMA ===== */}
      <section className="bv-ecosystem" data-testid="bv-ecosystem">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">O Ecossistema Completo</h2>
            <p className="bv-section-subtitle">
              Planilha de controle + Dashboards que cobrem todo o seu funil comercial
            </p>
          </div>
          <div className="bv-eco-grid">
            {ecosystemItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bv-eco-card" data-testid={`bv-eco-card-${i}`}>
                  <div className="bv-eco-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="bv-eco-title">{item.title}</h3>
                  <p className="bv-eco-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== DIFERENCIAL TÉCNICO ===== */}
      <section className="bv-differentials" data-testid="bv-differentials">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">Diferenciais Técnicos</h2>
            <p className="bv-section-subtitle">
              O que torna essa solução única no mercado e ideal para seu negócio
            </p>
          </div>
          <div className="bv-diff-grid">
            {differentials.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bv-diff-card" data-testid={`bv-diff-card-${i}`}>
                  <div className="bv-diff-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="bv-diff-title">{item.title}</h3>
                  <p className="bv-diff-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== BENEFÍCIOS ===== */}
      <section className="bv-benefits" data-testid="bv-benefits">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">O Que Você Vai Enxergar</h2>
            <p className="bv-section-subtitle">
              Dashboards que transformam dados em decisões lucrativas e otimizadas
            </p>
          </div>
          <div className="bv-benefits-grid">
            {benefits.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bv-benefit-card" data-testid={`bv-benefit-${i}`}>
                  <div className="bv-benefit-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="bv-benefit-title">{item.title}</h3>
                  <p className="bv-benefit-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PROVA SOCIAL / LOGOS ===== */}
      <section className="bv-social-proof" data-testid="bv-social-proof">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">Empresas que Já Utilizam</h2>
          </div>
        </div>
        <div className="bv-logos-track-wrapper">
          <div className="bv-logos-track">
            {[...clientLogos, ...clientLogos].map((name, i) => (
              <div key={i} className="bv-logo-item" data-testid={`bv-logo-${i}`}>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CARROSSEL DE IMAGENS ===== */}
      <section className="bv-showcase" data-testid="bv-showcase">
        <div className="bv-container">
          <div className="bv-section-header">
            <h2 className="bv-section-title">Veja a Solução em Ação</h2>
            <p className="bv-section-subtitle">
              Modelos do dashboards e da planilha que você irá adquirir
            </p>
          </div>
          <div className="portfolio-detail-container">
            <div className="portfolio-image-carousel">
              <img
                src={showcaseImages[currentImage]}
                alt={`Dashboard exemplo ${currentImage + 1}`}
                className="portfolio-carousel-image"
              />
              {showcaseImages.length > 1 && (
                <>
                  <button className="carousel-btn carousel-btn-prev" onClick={prevImage} aria-label="Imagem anterior" data-testid="bv-carousel-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button className="carousel-btn carousel-btn-next" onClick={nextImage} aria-label="Próxima imagem" data-testid="bv-carousel-next">
                    <ChevronRight size={24} />
                  </button>
                  <div className="carousel-indicators">
                    {showcaseImages.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImage ? 'active' : ''}`}
                        onClick={() => setCurrentImage(index)}
                        aria-label={`Ir para imagem ${index + 1}`}
                      />
                    ))}
                  </div>
                  <div className="carousel-counter">
                    {currentImage + 1} / {showcaseImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="bv-faq" data-testid="bv-faq">
        <div className="bv-container bv-faq-container">
          <div className="bv-section-header">
            <div className="bv-faq-icon-wrapper">
              <HelpCircle size={32} />
            </div>
            <h2 className="bv-section-title">Perguntas Frequentes</h2>
          </div>
          <div className="faq-grid-modern">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card-modern ${openFaq === index ? 'faq-card-open' : ''}`}
              >
                <button
                  className="faq-question-modern"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`bv-faq-question-${index}`}
                >
                  <span className="faq-number bv-faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${openFaq === index ? 'faq-chevron-open' : ''}`}
                  />
                </button>
                <div className={`faq-answer-modern ${openFaq === index ? 'faq-answer-visible' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="bv-final-cta" data-testid="bv-final-cta">
        <div className="bv-container">
          <div className="bv-final-cta-content">
            <h2 className="bv-final-cta-title">Pronto para ter Clareza Total do Seu Setor Comercial?</h2>
            <p className="bv-final-cta-text">
              Venha conversar comigo e ver como essa solução pode se encaixar na sua operação e trazer a clareza e os retornos que você merece!
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="bv-btn-primary" data-testid="bv-final-cta-btn">
              Quero Ter Clareza do Meu Setor Comercial
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default BiVendasPerformanceLanding;
